<script setup>
import { computed } from 'vue';
import { useRoute } from 'vue-router';

import Icon from '@/components/foundation/Icon.vue';
import { useIntentMap } from '@/components/VisionAi/composables/useIntentMap';
import {
  DEFAULT_METRIC_AND_ANALYTICS_PROMPTS,
  DEFAULT_SUMMARY_PROMPTS_BY_INTENT_TYPE,
  SUMMARY_INTENT_TYPES,
  VISION_AI_MOCK_PROMPT_ROUTES,
  VISION_AI_SUMMARY_LAUNCH_TYPE,
} from '@/components/VisionAi/constants';
import UserWrapper from '@/components/VisionAi/UserWrapper.vue';
import { stripHtmlFromMessage } from '@/components/VisionAi/utils/message';
import { aiSummaryLaunchEvent } from '@/components/VisionAi/utils/mixpanel-events';
import { usePageFilters } from '@/components/VisionAi/utils/request-context';
import { useVisionAiStore } from '@/stores/vision-ai';
import { useVisionAiConversationsStore } from '@/stores/vision-ai-conversations';
import { useVisionAiMessagesStore } from '@/stores/vision-ai-messages';

const visionAiStore = useVisionAiStore();
const visionAiMessagesStore = useVisionAiMessagesStore();
const visionAiConversationsStore = useVisionAiConversationsStore();
const route = useRoute();

const { checkIfInvalidThreshold } = useIntentMap();
const pageFilters = usePageFilters();

const props = defineProps({
  promptConfig: {
    type: Object,
    default: () => ({}),
  },
  disabled: {
    type: Boolean,
    default: false,
  },
  tooltip: {
    type: String,
    default: null,
  },
});

const tooltipProps = computed(() => {
  if (props.disabled) {
    return { content: props.tooltip, boundary: 'viewport', placement: 'top' };
  }

  return {
    content: props.tooltip,
    theme: 'dh-tooltip-center',
  };
});

async function handleUnifiedPromptButtonClicked() {
  visionAiStore.startNewChat();
  await visionAiStore.startConversation({
    message: props.promptConfig.message,
    intentTypeOverride: props.promptConfig.intentTypeOverride,
    buttonIdentifier: props.promptConfig.buttonIdentifier,
    filters: pageFilters.value,
  });
  aiSummaryLaunchEvent({
    launchedFrom: VISION_AI_SUMMARY_LAUNCH_TYPE.PROMPT,
    intentType: props.promptConfig.intentTypeOverride,
  });
}

async function handleSummaryPromptButtonClicked(isMockPrompt) {
  visionAiConversationsStore.selectSummaries();
  visionAiStore.clearLocalChatHistory();
  await visionAiMessagesStore.getMessages({ intentTypes: SUMMARY_INTENT_TYPES });

  if (checkIfInvalidThreshold(visionAiStore.currentIntentType) || isMockPrompt) {
    await visionAiStore.createMockMessages({ isMockPrompt });
  } else {
    await visionAiStore.startConversation({
      message: props.promptConfig.message,
      filters: pageFilters.value,
    });
  }
  aiSummaryLaunchEvent({
    launchedFrom: VISION_AI_SUMMARY_LAUNCH_TYPE.PROMPT,
    intentType: visionAiStore.currentIntentType,
  });
}

const isSummaryPrompt = computed(() =>
  Object.values(DEFAULT_SUMMARY_PROMPTS_BY_INTENT_TYPE).includes(props.promptConfig.message),
);

async function handlePromptButtonClicked() {
  if (props.disabled) return;

  if (props.promptConfig.intentTypeOverride) {
    await handleUnifiedPromptButtonClicked();
    return;
  }

  let isMockPrompt = false;

  isMockPrompt =
    VISION_AI_MOCK_PROMPT_ROUTES.includes(route?.name) &&
    !DEFAULT_METRIC_AND_ANALYTICS_PROMPTS.includes(props.promptConfig.message);

  if (isSummaryPrompt.value || isMockPrompt) {
    await handleSummaryPromptButtonClicked(isMockPrompt);
    return;
  }

  visionAiConversationsStore.setSummariesSelectedValue(false);
  await visionAiStore.startConversation({
    message: props.promptConfig.message,
    filters: pageFilters.value,
  });
}

const summaryPromptReady = computed(
  () =>
    Boolean(props.promptConfig.intentTypeOverride) ||
    (isSummaryPrompt.value && !checkIfInvalidThreshold(visionAiStore.currentIntentType)),
);
</script>

<template>
  <UserWrapper
    v-if="props.promptConfig.message"
    v-tooltip.top="tooltipProps"
    :summary-ready="summaryPromptReady"
    :is-disabled="disabled"
    @user-button-clicked="handlePromptButtonClicked"
  >
    <div
      class="!flex !h-[--space-18] !w-[--space-18] !items-center !justify-center !rounded-3xl !bg-[--brand-primary] !p-[--space-3]"
    >
      <Icon name="prompt-icon" :color="'white'" />
    </div>

    <span>{{ stripHtmlFromMessage(props.promptConfig.label || props.promptConfig.message) }}</span>
  </UserWrapper>
</template>
